import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Table } from "../../../../components/Table";
import { Api } from "../../../../services/api";
import {
  Team,
  TeamMember,
  TeamMembers,
  TeamType,
} from "../../../../services/api/types/Teams";
import { defaultSliderFilters } from "./columnData";
import { TeamForm } from "../TeamForm";
import {
  StatusOptions,
  TeamFormHeader,
  TeamFormType,
} from "../TeamForm/teamFormSchema";
import { TeamStatus, UserPermissions } from "../../../../enums";
import { makeName } from "../../../../services/hooks/MembersList";
import { getColumns } from "./columnData";
import {
  selectMemberMap,
  useAppDispatch,
  useAppSelector,
} from "../../../../store/hooks";
import { setMembers } from "../../../../store/slices/MemberListSlice";
import { ColumnContainer } from "../../Admin.styles";
import { makeTeamTypesMap } from "../../../../services/hooks/TeamTypeList";
import axios from "axios";
import { FormikHelpers } from "formik";

const defaultTeamInfo: TeamFormType = {
  status: undefined,
  team: "",
  nickName: "",
  lead: "",
};

const testData = new Array<TeamMember>();

export const EditTeam = () => {
  const dispatch = useAppDispatch();
  const userPermissions = useAppSelector(state => state.auth.user.permissions);
  const token = useAppSelector(state => state.auth.token);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState(testData);
  const selectedRows = useRef([]);

  const history = useHistory();
  const { teamId } = useParams<{ teamId: string }>();
  const [teamData, setTeamData] = useState(defaultTeamInfo);

  const members = selectMemberMap();
  const teamTypesArray = useAppSelector(state => state.teamTypes.teamTypes);
  const [teamTypes, setTeamTypes] = useState(new Map<string, TeamType>());

  let mounted = true;
  useEffect(() => {
    setIsLoading(true);
    if (teamTypesArray.length && mounted) {
      setTeamTypes(makeTeamTypesMap(teamTypesArray));
      fetchTeam(makeTeamTypesMap(teamTypesArray)).then(() => {
        setIsLoading(false);
      });
    }
    return () => {
      mounted = false;
    };
  }, [teamId, teamTypesArray]);

  const fetchTeam = async (teamTypes: Map<string, TeamType>) => {
    try {
      const response = await Api.teams.getTeamDetails(teamId);
      const data: TeamFormType = {
        status: StatusOptions[response.data.status ? 0 : 1],
        team: [...teamTypes.values()].find(
          teamType => teamType.id === response.data.teamTypeId
        )?.name,
        nickName: response.data.nickname,
        lead: makeName(response.data.teamLead),
      };
      if (mounted) {
        setTeamData(data);
        setTableData(response.data.members);
      }
    } catch (error) {
      console.error("Teams fetch:", error);
    }
  };

  const submitValues = (
    values: TeamFormType,
    { setErrors }: FormikHelpers<TeamFormType>
  ) => {
    const teamType = teamTypes.get(values.team as string);
    const teamLeader = members.get(values.lead as string);

    const data: Team = {
      status:
        values.status === "Active" ? TeamStatus.ACTIVE : TeamStatus.INACTIVE,
      nickname: values.nickName as string,
      teamTypeId: teamType ? teamType.id : "",
      teamLeaderId: teamLeader ? teamLeader.id : undefined,
    };

    try {
      Api.teams
        .updateTeamDetails(teamId, data)
        .then(() => {
          //update members now that one has been changed to team
          Api.members
            .getMembers()
            .then(res => {
              dispatch(setMembers(res.data));
            })
            .catch(() => {
              dispatch(setMembers([]));
            });
          const currentLocation = history.location;
          currentLocation.pathname = "/admin/teams";
          history.push(currentLocation);
        })
        .catch((error: Error) => {
          if (
            axios.isAxiosError(error) &&
            error.response &&
            error.response.data.detail ===
              "Team with this nickname already exists"
          ) {
            setErrors({ nickName: "Name already used" });
          } else {
            console.error("Create Office", error);
          }
        });
    } catch (error) {
      console.error("Update Team:", error);
    }
  };

  let handleAddTeamMember;
  if (userPermissions.includes(UserPermissions.TeamAddMembers)) {
    handleAddTeamMember = () => {
      const currentLocation = history.location;
      currentLocation.pathname = `/admin/teams/${teamId}/addMember`;
      history.push(currentLocation);
    };
  }

  const handleDeleteSelected = async () => {
    const newTeam: TeamMembers = {
      teamId: teamId,
      members: [],
    };

    if (!selectedRows.current.length) return;

    if (tableData.length !== selectedRows.current.length) {
      const filteredData = tableData.filter(
        (member: any) =>
          !selectedRows.current.find(
            (selectedMember: any) => selectedMember.id === member.id
          )
      );
      filteredData.forEach((member: any) => newTeam.members.push(member.id));
    }

    try {
      const res = await Api.teams.updateTeamMembers(teamId, newTeam);
      setTableData(oldData =>
        oldData.filter(member =>
          (res.data.members as unknown as Array<string>).includes(member.id)
        )
      );
    } catch (error) {
      console.error("Delete Team Members:", error);
    }
  };

  return (
    <ColumnContainer>
      {!isLoading ? (
        <>
          <TeamForm
            header={TeamFormHeader.edit}
            submitValues={submitValues}
            value={teamData}
            canSave={userPermissions.includes(
              UserPermissions.TeamManageDetails
            )}
          />
          <div style={{ width: "100%" }}>
            <Table
              searchable
              editable
              tableType="Members"
              isLoading={isLoading}
              defaultRows={!tableData.length ? 0 : 20}
              columns={getColumns(token)}
              rowsPerPage={[10, 20, 35, 50]}
              data={tableData}
              sliderFilters={defaultSliderFilters}
              selectedRows={selectedRows}
              handleDeleteSelected={handleDeleteSelected}
              handleAddTeamMember={handleAddTeamMember}
            />
          </div>
        </>
      ) : null}
    </ColumnContainer>
  );
};
